import { useEffect, useState } from 'react';
import Icon from '../components/icon';
import SelectField from '../components/select-field';
import TextField from '../components/text-field';
import {
  deleteUser,
  getUser,
  getUserAudit,
  listDivisions,
  listUsers,
  resendInvitation,
  resetUserPassword,
  restoreUser,
  updateUser,
} from '../api/panel-api';
import {
  eventLabels,
  emailErrorText,
  emailPattern,
  formatDateTime,
  formatPhone,
  fullName,
  isCompletePhone,
  phoneErrorText,
  initials,
  panelRoleLabel,
  panelRoles,
  serviceRoleLabel,
  serviceRoles,
  userFormFieldErrors,
} from '../data/panel';
import { Dialog } from './users-page';

const feedFilters = [
  { id: 'all', label: 'Все' },
  { id: 'comment', label: 'Комментарии' },
  { id: 'system', label: 'Системные' },
];

const statusLabel = {
  invited: 'Приглашён',
  active: 'Активен',
  deleted: 'Удалён',
};

const serviceOptions = serviceRoles.map((role) => (
  role.value === 'division_head' ? { value: 'rd', label: role.label } : role
));

function personName(person) {
  if (!person || typeof person !== 'object') return '';
  return [person.first_name, person.last_name].filter(Boolean).join(' ') || person.email || '';
}

function presentUser(item) {
  const status = item.is_deleted ? 'deleted' : item.is_invited ? 'invited' : 'active';
  return {
    id: item.id,
    firstName: item.first_name || '',
    lastName: item.last_name || '',
    email: item.email || '',
    phone: item.phone || '',
    position: item.job_title || '',
    roleCode: item.role,
    role: panelRoleLabel[item.role] || item.role,
    serviceRoleCode: item.cs_role || '',
    serviceRole: serviceRoleLabel[item.cs_role] || '',
    divisionId: item.division_id || '',
    assignedClients: item.assigned_clients_count,
    showEnv: Boolean(item.can_see_environment_switcher),
    status,
    sections: item.sections || [],
    createdAt: item.created_at,
    createdByName: personName(item.created_by),
    lastLoginAt: item.last_login_at,
  };
}

function formFrom(user, sectionCodes) {
  const admin = user.roleCode === 'administrator';
  return {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    phone: formatPhone(user.phone),
    position: user.position,
    role: user.roleCode,
    serviceRole: user.serviceRoleCode,
    divisionId: user.divisionId,
    sections: admin ? sectionCodes : user.sections.slice(),
    showEnvSwitcher: admin || user.showEnv,
  };
}

function snapshotForm(form) {
  if (!form) return '';
  return JSON.stringify({
    firstName: form.firstName,
    lastName: form.lastName,
    email: form.email,
    phone: form.phone,
    position: form.position,
    role: form.role,
    serviceRole: form.serviceRole,
    divisionId: form.divisionId,
    showEnvSwitcher: form.showEnvSwitcher,
    sections: [...form.sections].sort(),
  });
}

function creatorParts(user) {
  const when = formatDateTime(user.createdAt);
  return {
    name: user.createdByName || '',
    when: when.text !== '—' ? when.text : '',
  };
}

function UserBackLink({ onBack }) {
  return (
    <button type="button" className="page-back" onClick={onBack}>
      <Icon name="chevron-left" size={14} tone="inherit" />
      Пользователи
    </button>
  );
}

const feedMonths = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

function formatFeedWhen(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const stamp = formatDateTime(value);
  return `${date.getDate()} ${feedMonths[date.getMonth()]}, ${stamp.time}`;
}

function eventSearchText(event) {
  return [
    feedActor(event),
    event.comment,
    eventLabels[event.event_type],
    event.event_type,
  ].filter(Boolean).join(' ').toLowerCase();
}

function feedActor(event) {
  const name = personName(event.actor);
  return name || 'Система';
}

function isCommentEvent(event) {
  return Boolean(event.comment) || event.event_type === 'comment' || event.category === 'comment';
}

function AccessCheck({ label, checked, onChange, disabled }) {
  return (
    <label className="access-check">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} />
      <span>{label}</span>
    </label>
  );
}

function MetaItem({ label, children }) {
  return (
    <div className="user-meta__item">
      <span>{label}</span>
      <p className="user-meta__value">{children}</p>
    </div>
  );
}

function UserCardPage({
  userId,
  currentUserId,
  sections = [],
  onBack = () => {},
  onOpenManagers = () => {},
}) {
  const [user, setUser] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [menu, setMenu] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [comment, setComment] = useState('');
  const [pending, setPending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [feed, setFeed] = useState('all');
  const [feedQuery, setFeedQuery] = useState('');
  const [events, setEvents] = useState([]);
  const [eventError, setEventError] = useState('');
  const [eventPage, setEventPage] = useState(1);
  const [eventPageSize, setEventPageSize] = useState(30);
  const [eventTotal, setEventTotal] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [rksName, setRksName] = useState('');
  const [divisions, setDivisions] = useState([]);
  const [divisionError, setDivisionError] = useState('');

  const sectionCodes = sections.map((item) => item.code);
  const self = Boolean(user && currentUserId && user.id === currentUserId);
  const deleted = user && user.status === 'deleted';
  const adminRole = form && form.role === 'administrator';
  const divisionHead = form && form.serviceRole === 'rd';

  useEffect(() => {
    let cancelled = false;
    setError('');
    getUser(userId)
      .then((item) => {
        if (cancelled) return;
        const next = presentUser(item);
        setUser(next);
        setForm(formFrom(next, sectionCodes));
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, reloadKey]);

  useEffect(() => {
    if (!user || user.roleCode !== 'administrator' || !sectionCodes.length) return;
    setForm((current) => (current ? { ...current, sections: sectionCodes, showEnvSwitcher: true } : current));
  }, [user, sectionCodes.join('|')]);

  useEffect(() => {
    let cancelled = false;
    setEventError('');
    getUserAudit(userId, {
      page: eventPage,
      perPage: eventPageSize,
      category: feed === 'all' ? undefined : feed,
    })
      .then((data) => {
        if (cancelled) return;
        setEvents(data.items || []);
        setEventTotal(data.total || 0);
      })
      .catch((loadError) => {
        if (!cancelled) setEventError(loadError.message);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, feed, eventPage, eventPageSize, reloadKey]);

  useEffect(() => {
    if (!menu) return undefined;
    function close(event) {
      if (event.target.closest('.user-card__tools')) return;
      setMenu(false);
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menu]);

  useEffect(() => {
    let cancelled = false;
    async function loadRks() {
      let page = 1;
      let total = 0;
      do {
        const data = await listUsers({ page, perPage: 100, order: 'asc', filters: {} });
        total = data.total || 0;
        const holder = (data.items || []).find((item) => item.cs_role === 'rks' && item.id !== userId);
        if (holder) {
          if (!cancelled) setRksName(`${holder.first_name} ${holder.last_name}`);
          return;
        }
        page += 1;
      } while ((page - 1) * 100 < total);
    }
    loadRks().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    listDivisions()
      .then((data) => {
        if (!cancelled) setDivisions(data.items || []);
      })
      .catch((loadError) => {
        if (!cancelled) setDivisionError(loadError.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function setField(key, value) {
    const nextValue = key === 'phone' ? formatPhone(value) : value;
    setForm((current) => ({ ...current, [key]: nextValue }));
    setFieldErrors((current) => ({ ...current, [key]: '' }));
    setFormError('');
  }

  function setRole(value) {
    setForm((current) => ({
      ...current,
      role: value,
      serviceRole: value === 'cs_employee' ? current.serviceRole : '',
      divisionId: value === 'cs_employee' ? current.divisionId : '',
      showEnvSwitcher: value === 'administrator'
        ? true
        : (current.role === 'administrator' ? false : current.showEnvSwitcher),
      sections: value === 'administrator'
        ? sectionCodes
        : (current.role === 'administrator'
          ? (sectionCodes.includes('dashboard') ? ['dashboard'] : [])
          : current.sections),
    }));
    setFormError('');
  }

  function toggleSection(code) {
    if (adminRole || self) return;
    setForm((current) => ({
      ...current,
      sections: current.sections.includes(code)
        ? current.sections.filter((item) => item !== code)
        : [...current.sections, code],
    }));
  }

  function open(type, extra) {
    setMenu(false);
    setDialogError('');
    setComment('');
    setDialog({ type, user: { ...user, ...extra } });
  }

  function askDelete() {
    if (user.assignedClients > 0) {
      open('delete-blocked', {
        blockDetail: user.assignedClients ? `${user.assignedClients} клиентов` : user.email,
      });
      return;
    }
    open('delete');
  }

  async function confirmDialog() {
    if (!dialog) return;
    if (dialog.type === 'delete-blocked' || dialog.type === 'role') {
      setDialog(null);
      onOpenManagers();
      return;
    }
    setPending(true);
    setDialogError('');
    try {
      if (dialog.type === 'invite') await resendInvitation(user.id);
      if (dialog.type === 'reset') await resetUserPassword(user.id);
      if (dialog.type === 'delete') await deleteUser(user.id);
      if (dialog.type === 'restore') await restoreUser(user.id, comment);
      setDialog(null);
      setReloadKey((value) => value + 1);
    } catch (actionError) {
      if (dialog.type === 'delete' && actionError.status === 409) {
        open('delete-blocked', { blockDetail: actionError.message });
        return;
      }
      setDialogError(actionError.message);
    } finally {
      setPending(false);
    }
  }

  function cancelEdit() {
    if (!user) return;
    setForm(formFrom(user, sectionCodes));
    setFieldErrors({});
    setFormError('');
  }

  async function save() {
    if (!form || !user || deleted) return;
    const nextFieldErrors = {};
    if (!emailPattern.test(form.email)) nextFieldErrors.email = emailErrorText;
    if (!isCompletePhone(form.phone)) nextFieldErrors.phone = phoneErrorText;
    if (Object.keys(nextFieldErrors).length) {
      setFieldErrors((current) => ({ ...current, ...nextFieldErrors }));
      return;
    }
    const leavingService = user.roleCode === 'cs_employee' && form.role !== 'cs_employee';
    const toConsultant = form.role === 'cs_employee' && form.serviceRole === 'consultant' && user.serviceRoleCode !== 'consultant';
    if ((leavingService || toConsultant) && user.assignedClients > 0) {
      open('role', { blockDetail: `${user.assignedClients} клиентов` });
      return;
    }
    setSaving(true);
    setFormError('');
    setFieldErrors({});
    const body = {
      first_name: form.firstName,
      last_name: form.lastName,
      email: form.email,
      phone: form.phone,
      job_title: form.position,
      role: form.role,
      can_see_environment_switcher: form.role === 'administrator' ? true : form.showEnvSwitcher,
    };
    if (form.role === 'administrator') {
      if (sectionCodes.length) body.sections = sectionCodes;
    } else {
      body.sections = form.sections;
    }
    if (form.role === 'cs_employee') body.cs_role = form.serviceRole;
    if (form.serviceRole === 'rd' || (form.serviceRole === 'manager' && form.divisionId)) {
      body.division_id = form.divisionId;
    }
    try {
      const saved = presentUser(await updateUser(user.id, body));
      setUser(saved);
      setForm(formFrom(saved, sectionCodes));
      setReloadKey((value) => value + 1);
    } catch (actionError) {
      if (actionError.status === 409 && /клиент/i.test(actionError.message || '')) {
        open('role', { blockDetail: actionError.message });
        return;
      }
      const mapped = userFormFieldErrors(actionError);
      setFormError(mapped.banner);
      setFieldErrors(mapped.fields);
    } finally {
      setSaving(false);
    }
  }

  if (error) {
    return (
      <section className="user-page" aria-label="Пользователь">
        <UserBackLink onBack={onBack} />
        <div className="empty"><p>{error}</p></div>
      </section>
    );
  }

  if (!user || !form) {
    return (
      <section className="user-page" aria-label="Пользователь" aria-busy="true">
        <UserBackLink onBack={onBack} />
      </section>
    );
  }

  const work = sections.filter((item) => item.group === 'work');
  const administration = sections.filter((item) => item.group === 'administration');
  const known = new Set(sections.map((item) => item.code));
  const extra = form.sections.filter((code) => !known.has(code));
  const roleChoices = serviceOptions.map((role) => {
    if (role.value !== 'rks' || !rksName) return role;
    return { ...role, label: 'Руководитель КС', note: rksName, disabled: true };
  });
  const needsDivision = divisionHead || form.serviceRole === 'manager';
  const divisionChoices = divisions.length
    ? divisions.map((item) => {
      const taken = item.head_user_id && item.head_user_id !== user.id;
      const head = personName(item.head);
      return {
        value: item.id,
        label: item.title,
        note: taken && head ? head : undefined,
        disabled: Boolean(taken),
      };
    })
    : [{ value: '', label: 'Нет дивизионов', disabled: true }];
  if (form.divisionId && !divisionChoices.some((item) => item.value === form.divisionId)) {
    divisionChoices.unshift({ value: form.divisionId, label: 'Текущий дивизион' });
  }
  const locked = self;
  const baseline = snapshotForm(formFrom(user, sectionCodes));
  const dirty = snapshotForm(form) !== baseline;
  const canSave = dirty
    && form.firstName && form.lastName && form.email && form.phone && form.position && form.role
    && (form.role !== 'cs_employee' || form.serviceRole)
    && (!divisionHead || form.divisionId)
    && (adminRole || form.sections.length > 0)
    && !saving
    && !locked;
  const query = feedQuery.trim().toLowerCase();
  const visibleEvents = query
    ? events.filter((event) => eventSearchText(event).includes(query))
    : events;
  const eventFrom = eventTotal ? (eventPage - 1) * eventPageSize + 1 : 0;
  const eventTo = Math.min(eventPage * eventPageSize, eventTotal);
  const eventPageCount = Math.max(1, Math.ceil(eventTotal / eventPageSize));
  const roleValue = deleted
    ? [user.role, user.serviceRole].filter(Boolean).join(' · ')
    : user.role;
  const loginStamp = formatDateTime(user.lastLoginAt);
  const loginText = loginStamp.date ? loginStamp.text : 'Ещё не входил';
  const created = creatorParts(user);

  return (
    <section className="user-page" aria-label={fullName(user)}>
      <UserBackLink onBack={onBack} />
      <div className="user-card-stack">
        <article className="card user-card">
          <div className="user-card__intro">
            <div className="card__head">
              <div className="user-card__identity">
                <h1 className="card-title">{fullName(user)}</h1>
                <span className={`user-status user-status--${user.status}`}>{statusLabel[user.status]}</span>
                {user.serviceRoleCode === 'rks' || user.serviceRoleCode === 'rd' ? (
                  <span className={`user-role-chip user-role-chip--${user.serviceRoleCode}`}>{user.serviceRole}</span>
                ) : null}
              </div>
              {deleted ? (
                <button type="button" className="btn btn--secondary btn--small" onClick={() => open('restore')}>Восстановить</button>
              ) : null}
              {!deleted && !self ? (
                <div className="user-card__tools">
                  <button type="button" className={menu ? 'icon-button is-pressed' : 'icon-button'} aria-label="Действия" aria-expanded={menu} onClick={() => setMenu((openMenu) => !openMenu)}>
                    <span className="kebab" aria-hidden="true"><i /><i /><i /></span>
                  </button>
                  {menu ? (
                    <div className={user.status === 'invited' ? 'row-menu row-menu--card row-menu--invite' : 'row-menu row-menu--card'}>
                      {user.status === 'invited' ? (
                        <button type="button" className="menu-row" onClick={() => open('invite')}>
                          <Icon name="send" size={16} tone="inherit" />
                          Отправить приглашение повторно
                        </button>
                      ) : (
                        <button type="button" className="menu-row" onClick={() => open('reset')}>
                          <Icon name="key" size={16} tone="inherit" />
                          Сбросить пароль
                        </button>
                      )}
                      {user.status === 'invited' ? null : <div className="divider" />}
                      <button type="button" className="menu-row menu-row--danger" onClick={askDelete}>
                        <Icon name="trash" size={16} tone="inherit" />
                        Удалить пользователя
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="user-meta">
              {deleted ? <MetaItem label="Роль в панели">{roleValue}</MetaItem> : null}
              <MetaItem label="Последний вход">{loginText}</MetaItem>
              <MetaItem label="Завёл">
                {created.name ? <strong>{created.name}</strong> : null}
                {created.name && created.when ? ', ' : null}
                {created.when ? <span className="user-meta__when">{created.when}</span> : null}
                {!created.name && !created.when ? '—' : null}
              </MetaItem>
            </div>
          </div>
          {deleted ? null : (
            <>
              {formError ? <p className="user-card__hint user-card__hint--error">{formError}</p> : null}
              <div className="user-card__fields">
                <div className="field-row">
                  <TextField label="Имя" value={form.firstName} error={fieldErrors.firstName} readOnly={locked} onChange={(value) => setField('firstName', value)} />
                  <TextField label="Фамилия" value={form.lastName} error={fieldErrors.lastName} readOnly={locked} onChange={(value) => setField('lastName', value)} />
                </div>
                <div className="field-row">
                  <TextField label="Электронная почта" value={form.email} error={fieldErrors.email} readOnly={locked} onChange={(value) => setField('email', value)} />
                  <TextField label="Телефон" value={form.phone} error={fieldErrors.phone} readOnly={locked} onChange={(value) => setField('phone', value)} />
                </div>
                <div className="field-row field-row--half">
                  <TextField label="Должность" value={form.position} error={fieldErrors.position} readOnly={locked} onChange={(value) => setField('position', value)} />
                </div>
                <div className="user-card__roles">
                  <div className="user-card__role">
                    <SelectField
                      label="Роль в панели"
                      value={form.role}
                      onChange={setRole}
                      options={panelRoles}
                      disabled={locked}
                    />
                    {self ? <p className="user-card__hint">Свою роль понизить нельзя</p> : null}
                    {fieldErrors.role ? <p className="user-card__hint user-card__hint--error">{fieldErrors.role}</p> : null}
                  </div>
                  {form.role === 'cs_employee' ? (
                    <div className="user-card__role">
                      <SelectField
                        label="Роль в клиентской службе"
                        value={form.serviceRole}
                        onChange={(value) => {
                          setForm((current) => ({
                            ...current,
                            serviceRole: value,
                            divisionId: value === 'rd' || value === 'manager' ? current.divisionId : '',
                          }));
                          setFormError('');
                        }}
                        options={roleChoices}
                        disabled={locked}
                      />
                      {fieldErrors.serviceRole ? <p className="user-card__hint user-card__hint--error">{fieldErrors.serviceRole}</p> : null}
                    </div>
                  ) : null}
                  {needsDivision ? (
                    <div className="user-card__role">
                      <SelectField
                        label="Дивизион"
                        value={form.divisionId}
                        onChange={(value) => setField('divisionId', value)}
                        options={divisionChoices}
                        disabled={locked}
                      />
                      {fieldErrors.divisionId || divisionError ? (
                        <p className="user-card__hint user-card__hint--error">{fieldErrors.divisionId || divisionError}</p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="section-access">
                <h2 className="section-access__title">Открытые разделы</h2>
                {fieldErrors.sections ? <p className="user-card__hint user-card__hint--error">{fieldErrors.sections}</p> : null}
                <div className="section-access__columns">
                  <div className="section-access__col">
                    <p className="section-access__heading">Работа</p>
                    {work.map((item) => (
                      <AccessCheck
                        key={item.code}
                        label={item.title}
                        checked={adminRole || form.sections.includes(item.code)}
                        disabled={adminRole || locked}
                        onChange={() => toggleSection(item.code)}
                      />
                    ))}
                  </div>
                  <div className="section-access__col">
                    <p className="section-access__heading">Администрирование</p>
                    {administration.map((item) => (
                      <AccessCheck
                        key={item.code}
                        label={item.title}
                        checked={adminRole || form.sections.includes(item.code)}
                        disabled={adminRole || locked}
                        onChange={() => toggleSection(item.code)}
                      />
                    ))}
                    {extra.map((code) => (
                      <AccessCheck
                        key={code}
                        label={code}
                        checked
                        disabled={adminRole || locked}
                        onChange={() => toggleSection(code)}
                      />
                    ))}
                    <AccessCheck
                      label="Видеть переключатель окружения"
                      checked={adminRole || form.showEnvSwitcher}
                      disabled={adminRole || locked}
                      onChange={() => setField('showEnvSwitcher', !form.showEnvSwitcher)}
                    />
                  </div>
                </div>
                {adminRole ? (
                  <div className="note note--info">
                    <Icon name="info" size={20} />
                    <p>Администратору открыты все разделы, включая те, что появятся позже.</p>
                  </div>
                ) : null}
                {form.role === 'guest' ? (
                  <div className="note note--info">
                    <Icon name="info" size={20} />
                    <p>Гость видит выбранные разделы, но ничего в них не делает: не меняет данные, не назначает менеджеров и не комментирует.</p>
                  </div>
                ) : null}
              </div>
              <div className="user-card__actions">
                <button type="button" className="btn btn--secondary" onClick={cancelEdit}>Отмена</button>
                <button type="button" className="btn" disabled={!canSave} onClick={save}>Сохранить</button>
              </div>
            </>
          )}
        </article>
        <article className="card feed-card">
          <h2 className="section-title">Лента событий</h2>
          <div className="feed-toolbar">
            <div className="feed-switch" role="tablist" aria-label="Лента событий">
              {feedFilters.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={feed === item.id}
                  className={feed === item.id ? 'feed-switch__item is-active' : 'feed-switch__item'}
                  onClick={() => { setFeed(item.id); setEventPage(1); }}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="feed-toolbar__actions">
              <label className="feed-search feed-search--box">
                <Icon name="search" size={16} />
                <input
                  type="search"
                  value={feedQuery}
                  placeholder="Поиск по ленте"
                  onChange={(event) => setFeedQuery(event.target.value)}
                />
                {feedQuery ? (
                  <button type="button" className="feed-search__clear" aria-label="Очистить поиск" onClick={() => setFeedQuery('')}>
                    <Icon name="x" size={14} />
                  </button>
                ) : null}
              </label>
              {deleted ? null : (
                <button type="button" className="btn btn--secondary">
                  <Icon name="plus" size={16} />
                  Добавить комментарий
                </button>
              )}
            </div>
          </div>
          {eventError ? <p className="feed-empty">{eventError}</p> : null}
          {!eventError && visibleEvents.length === 0 ? (
            <div className="empty feed-empty-state">
              <div className="empty__icon">
                <Icon name={query ? 'filter' : feed === 'comment' ? 'note' : 'clock'} size={24} tone="inherit" />
              </div>
              <div className="empty__copy">
                <p className="empty__title">
                  {query
                    ? 'Ничего не найдено'
                    : feed === 'comment'
                      ? 'Комментариев пока нет'
                      : 'Событий пока нет'}
                </p>
                {query ? <p className="empty__lead">Измените запрос или очистите поиск</p> : null}
              </div>
              {query ? (
                <button type="button" className="btn btn--secondary" onClick={() => setFeedQuery('')}>Очистить поиск</button>
              ) : null}
            </div>
          ) : null}
          {visibleEvents.length ? (
          <div className="feed-list">
            {visibleEvents.map((event) => {
              const actor = feedActor(event);
              const comment = isCommentEvent(event);
              const title = comment
                ? (event.comment || eventLabels[event.event_type] || event.event_type)
                : (eventLabels[event.event_type] || event.event_type);
              return (
                <article className="feed-entry" key={event.id}>
                  {comment ? (
                    <span className={`feed-entry__avatar feed-entry__avatar--${(actor.charCodeAt(0) || 0) % 2}`}>
                      {initials(event.actor?.first_name, event.actor?.last_name) || '•'}
                    </span>
                  ) : (
                    <span className="feed-entry__mark">
                      <Icon name="clock" size={16} />
                    </span>
                  )}
                  <div className="feed-entry__body">
                    <span>
                      <strong>{actor}</strong>
                      {' '}
                      <time>{formatFeedWhen(event.occurred_at)}</time>
                    </span>
                    <p className="feed-entry__text">{title}</p>
                  </div>
                </article>
              );
            })}
          </div>
          ) : null}
          {visibleEvents.length ? (
          <footer className="pager">
            <label className="pager__size">
              Показывать по
              <select value={eventPageSize} onChange={(event) => { setEventPageSize(Number(event.target.value)); setEventPage(1); }}>
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
            <div className="pager__nav">
              <span>{eventTotal ? `${eventFrom}–${eventTo} из ${eventTotal}` : '0 из 0'}</span>
              <button type="button" className="icon-button" aria-label="Предыдущая страница" disabled={eventPage <= 1} onClick={() => setEventPage((value) => value - 1)}>
                <Icon name="chevron-left" size={16} />
              </button>
              <span className="counter">{eventPage}</span>
              <button type="button" className="icon-button" aria-label="Следующая страница" disabled={eventPage >= eventPageCount} onClick={() => setEventPage((value) => value + 1)}>
                <Icon name="chevron-right" size={16} />
              </button>
            </div>
          </footer>
          ) : null}
        </article>
      </div>
      {dialog ? (
        <Dialog
          dialog={dialog}
          comment={comment}
          onComment={setComment}
          pending={pending}
          error={dialogError}
          onClose={() => setDialog(null)}
          onConfirm={confirmDialog}
        />
      ) : null}
    </section>
  );
}

export default UserCardPage;
